import { useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { DownloadIcon, PlayIcon, ChevronUpIcon } from '@heroicons/react/solid'
import { fetchReturnProofVideos } from '../../services/returnServices'

// While the platform reports a kept recording still uploading, the list is asked again on this
// cadence. Otherwise it is never refreshed on its own: a new signed URL reloads an open player.
const PENDING_POLL_MS = 15 * 1000
// A Download this close to the URL's expiry signs a fresh one first.
const EXPIRES_SOON_MS = 2 * 60 * 1000

const formatMegabytes = bytes => (bytes / (1024 * 1024)).toFixed(1)
const msUntilExpiry = video => new Date(video.expires_at).getTime() - Date.now()

export const ReturnProofVideos = ({ returnId }) => {
  const { i18n, t } = useTranslation()
  // key -> { url, expires_at } of an open player. Taken when it opens and replaced only when that
  // URL expired, so a refetch (the pending poll, a Download) never reloads a playing video.
  const [playerSources, setPlayerSources] = useState({})
  const [playbackFailed, setPlaybackFailed] = useState({})
  // A Download whose refresh no longer lists the video: its row is gone, so this says why.
  const [unavailable, setUnavailable] = useState(false)
  const players = useRef({})
  // Where each player was when its URL was replaced, so the reload resumes there.
  const resumeAt = useRef({})
  const { isLoading, isLoadingError, isRefetchError, data, refetch } = useQuery(['return_proof_videos', returnId], () => fetchReturnProofVideos(returnId), {
    enabled: returnId !== undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: current => (current?.pending ? PENDING_POLL_MS : false),
  })

  if (isLoading) return <i>{t('returns.proof_videos.loading')}</i>
  if (isLoadingError) return <i>{t('returns.proof_videos.error')}</i>

  const videos = data.videos
  if (videos.length === 0) return <i>{t(data.pending ? 'returns.proof_videos.processing' : 'returns.proof_videos.empty')}</i>

  const formatRecordedAt = value =>
    new Date(value).toLocaleString(i18n.language, { year: '2-digit', month: 'numeric', day: '2-digit', hour: '2-digit', minute: '2-digit' })

  // An expired link is re-signed and only that player takes the new URL, resuming where it was;
  // any other failure is this video's own, said on its row.
  const handlePlayerError = video => {
    const source = playerSources[video.key]
    if (!source || msUntilExpiry(source) > 0) {
      setPlaybackFailed(current => ({ ...current, [video.key]: true }))
      return
    }
    const player = players.current[video.key]
    if (player) resumeAt.current[video.key] = { time: player.currentTime, playing: !player.paused }
    refetch().then(result => {
      const fresh = result.data?.videos?.find(candidate => candidate.key === video.key)
      if (fresh) setPlayerSources(current => ({ ...current, [video.key]: { url: fresh.url, expires_at: fresh.expires_at } }))
    })
  }

  const handleLoadedMetadata = key => {
    const saved = resumeAt.current[key]
    const player = players.current[key]
    if (!saved || !player) return
    delete resumeAt.current[key]
    player.currentTime = saved.time
    if (saved.playing) player.play().catch(() => {})
  }

  // An attachment URL saves the file without leaving the page; one about to expire is re-signed
  // first, and a failed refresh never sends the browser to an expired link.
  const download = (event, video) => {
    if (msUntilExpiry(video) > EXPIRES_SOON_MS) return
    event.preventDefault()
    refetch().then(result => {
      if (result.isError) return
      const fresh = result.data?.videos?.find(candidate => candidate.key === video.key)
      if (fresh) window.location.assign(fresh.download_url)
      else setUnavailable(true)
    })
  }

  const togglePlayer = video =>
    setPlayerSources(current => {
      if (current[video.key]) return Object.fromEntries(Object.entries(current).filter(([key]) => key !== video.key))
      return { ...current, [video.key]: { url: video.url, expires_at: video.expires_at } }
    })
  const labelId = key => `proof-video-${key}-label`

  return (
    <div>
      {/* Rendered empty so a screen reader announces the text when it appears. */}
      <p role="status" className="text-sm text-red-600">
        {isRefetchError ? t('returns.proof_videos.refresh_error') : unavailable ? t('returns.proof_videos.unavailable') : null}
      </p>
      <ul className="border border-gray-200 rounded-md divide-y divide-gray-200">
        {videos.map(video => (
          <li key={video.key} className="pl-3 pr-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span id={labelId(video.key)} className="text-gray-700">
                {t('returns.proof_videos.recorded_at', { date: formatRecordedAt(video.uploaded_at), size: formatMegabytes(video.size) })}
              </span>
              <span className="flex gap-2">
                <a
                  href={video.download_url}
                  onClick={event => download(event, video)}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <DownloadIcon className="h-4 w-4" aria-hidden="true" />
                  {t('returns.proof_videos.download')}
                </a>
                <button
                  type="button"
                  onClick={() => togglePlayer(video)}
                  aria-expanded={!!playerSources[video.key]}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  {playerSources[video.key] ? <ChevronUpIcon className="h-4 w-4" aria-hidden="true" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" />}
                  {playerSources[video.key] ? t('returns.proof_videos.hide') : t('returns.proof_videos.watch')}
                </button>
              </span>
            </div>
            {playerSources[video.key] && (
              <>
                <video
                  ref={player => {
                    players.current[video.key] = player
                  }}
                  controls
                  preload="metadata"
                  src={playerSources[video.key].url}
                  aria-labelledby={labelId(video.key)}
                  onError={() => handlePlayerError(video)}
                  onLoadedMetadata={() => handleLoadedMetadata(video.key)}
                  className="mt-3 w-full max-w-xl rounded-lg bg-black"
                />
                <p role="status" className="mt-2 text-sm text-red-600">
                  {playbackFailed[video.key] ? t('returns.proof_videos.playback_failed') : null}
                </p>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
